<template>
    <main :class="$style.page">
        <template v-if="userInfoLoaded">
            <template v-if="user">
                <NativeAppDebugTool
                    v-if="isNativeAppDebugToolOpen"
                    @close="closeNativeAppDebugTool"
                />
                <template v-else>
                    <div :class="$style.caption_block">
                        <Typography
                            is="h1"
                            type="header"
                            size="m"
                            :responsive="false"
                        >
                            {{ $tr('userProfile.page_title') }}
                        </Typography>

                        <div
                            v-if="showGoldBalance"
                            :class="$style.user_balance"
                        >
                            <Coin size="large" />
                            <div :class="$style.user_balance__text">
                                <!-- eslint-disable-next-line vuejs-accessibility/heading-has-content -->
                                <Typography
                                    is="h2"
                                    type="header"
                                    size="xxl"
                                    :class="$style.user_balance_value"
                                    :responsive="false"
                                >
                                    <Balance
                                        v-if="user?._id"
                                        :balance="goldFenecBalance"
                                        isAnimated
                                        isColorBalance
                                    />
                                </Typography>
                            </div>
                            <Button
                                v-if="appStore.isPurchaseEnabled"
                                size="l"
                                color="golden"
                                interfaceTarget="buy-golden-coins-button"
                                :class="$style.buy_button_override"
                                :label="$tr('userProfile.buy_coins')"
                                @click="showPayment"
                            />
                        </div>

                        <Typography
                            type="paragraph"
                            size="s"
                            :class="$style.description"
                        >
                            {{ $tr('userProfile.page_description') }}
                        </Typography>

                        <Button
                            v-if="isNativeAppDebugToolAvailable"
                            label="Native app debug"
                            color="carbon-400"
                            interfaceTarget="native-app-debug-open"
                            :to="{ name: ROUTE_NAMES.PROFILE, hash: NATIVE_APP_DEBUG_HASH }"
                            :class="$style.employee_tool_button_override"
                        />
                    </div>
                </template>
            </template>
            <section
                v-else
                :class="$style.non_auth_title_page"
            >
                <Typography
                    is="h2"
                    type="header"
                    size="m"
                    :responsive="false"
                >
                    {{ $tr('userProfile.page_title_non_auth') }}
                </Typography>
                <Typography
                    type="paragraph"
                    size="s"
                    :class="$style.description"
                >
                    {{ $tr('userProfile.page_description_non_auth') }}
                </Typography>
                <SocialLogins
                    type="full"
                    loginSource="user-profile-page"
                    row
                    :class="$style.social_logins_override"
                />
            </section>
        </template>

        <div v-if="isMounted && myUnzippedGames.length > 0 && !isNativeAppDebugToolOpen">
            <ShelfTitle
                :title="$tr('myGames.title')"
                categoryId="heart"
            />
            <div :class="$style.my_games_grid">
                <GameCard
                    v-for="game in pagedMyGames"
                    :key="game.hru"
                    data-interface-target="game"
                    :game="game"
                    type="rectangle"
                    responsive
                    lazy
                    :targetLink="undefined"
                />
            </div>
            <PageToggle
                v-if="myGamesTotalPages > 1"
                :currentPage="myGamesPage"
                :totalPages="myGamesTotalPages"
                :getPageLink="getMyGamesPageLink"
                :class="$style.page_toggle_override"
            />
        </div>

        <div v-if="!isNativeAppDebugToolOpen">
            <ShelfTitle
                :title="trendingCategory.title"
                :link="trendingCategory.location"
                categoryId="trending_now"
            />
            <GeneralShelf
                :games="trendingGames.slice(0, 9)"
                :link="trendingCategory.location"
                :gameLinkTarget="undefined"
                embedded
                :categoryName="trendingCategory.title"
            />
        </div>

        <div
            v-if="userInfoLoaded && user && !isNativeAppDebugToolOpen"
            :class="$style.logout_button_wrapper"
        >
            <Button
                leftIcon="logout"
                size="l"
                :label="$tr('userProfile.logout_button')"
                color="rose-100"
                :class="$style.logout_button_override"
                interfaceTarget="logout"
                @click="logout"
            />
            <div :class="$style.logout_info">
                <Typography
                    type="paragraph"
                    size="s"
                    :responsive="false"
                    :class="$style.logout_info_text"
                >
                    {{ $tr('userProfile.logout_info_text') }}
                    <span
                        role="button"
                        tabindex="0"
                        data-interface-target="delete-account"
                        :class="$style.delete_account_link_override"
                        @click="deleteAccountModalOpen = true"
                        @keydown.enter.space="deleteAccountModalOpen = true"
                        >{{ $tr('userProfile.delete_account_link') }}</span
                    >
                </Typography>
            </div>
        </div>
    </main>
    <ModalDeleteAccount
        :isOpen="deleteAccountModalOpen"
        @close="deleteAccountModalOpen = false"
        @confirm="deleteAccount"
    />
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { computed, onServerPrefetch, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import Balance from '@/components/Balance.vue'
import Button from '@/components/Buttons/Button.vue'
import Coin from '@/components/Coin/Coin.vue'
import GameCard from '@/components/GameCard/GameCard.vue'
import GeneralShelf from '@/components/GamesShelf/GeneralShelf.vue'
import ShelfTitle from '@/components/GamesShelf/ShelfTitle.vue'
import { ModalDeleteAccount } from '@/components/Modals/async/ModalDeleteAccount'
import PageToggle from '@/components/PageToggle/PageToggle.vue'
import SocialLogins from '@/components/SocialLogins/SocialLogins.vue'
import Typography from '@/components/Typography.vue'
import { usePageQuery } from '@/composables/use-page-query'
import { useUserInfoLoaded } from '@/composables/use-user-state'
import { DEFAULT_CATEGORY_PAGE_SIZE } from '@/constants/general'
import { useTrendingData } from '@/query/use-trending-data'
import { ROUTE_NAMES } from '@/router/constants'
import { pageQuery } from '@/router/query-params'
import { useAppStore } from '@/store/app-store'
import { useLayoutStore } from '@/store/layout-store'
import { useMyGamesStore } from '@/store/my-games/my-games-store'
import { useUserStore } from '@/store/user-store/user-store'
import { useIsMounted } from '@/utils/vue-hooks/use-is-mounted'

import NativeAppDebugTool from './NativeAppDebugTool.vue'

const NATIVE_APP_DEBUG_HASH = '#native-app-debug'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const layoutStore = useLayoutStore()
const myGamesStore = useMyGamesStore()

const { myUnzippedGames } = storeToRefs(myGamesStore)
const {
    currentTrending: trendingCategory,
    unzippedTrendingWithGames: trendingGames,
    hasError: trendingHasError,
    preloadTrendingData,
} = useTrendingData()

const { user } = storeToRefs(userStore)
const userInfoLoaded = useUserInfoLoaded()
const isMounted = useIsMounted()

const requestedPage = usePageQuery()
const myGamesTotalPages = computed(() => Math.ceil(myUnzippedGames.value.length / DEFAULT_CATEGORY_PAGE_SIZE))
const myGamesPage = computed(() => Math.min(requestedPage.value || 1, myGamesTotalPages.value || 1))
const pagedMyGames = computed(() =>
    myUnzippedGames.value.slice(
        (myGamesPage.value - 1) * DEFAULT_CATEGORY_PAGE_SIZE,
        myGamesPage.value * DEFAULT_CATEGORY_PAGE_SIZE,
    ),
)

function getMyGamesPageLink(page: number) {
    return {
        name: ROUTE_NAMES.PROFILE,
        params: route.params,
        query: pageQuery(page),
    }
}

const deleteAccountModalOpen = ref(false)

const showGoldBalance = computed(() => user.value?.goldFenecBalance !== undefined && user.value.goldFenecBalance !== -1)
const goldFenecBalance = computed(() => userStore.user?.goldFenecBalance || 0)
const appStore = useAppStore()
const isNativeAppDebugToolAvailable = computed(() => userStore.isCompanyEmployeeLoggedIn)
const isNativeAppDebugToolOpen = computed(
    () => route.hash === NATIVE_APP_DEBUG_HASH && isNativeAppDebugToolAvailable.value,
)

function showPayment() {
    layoutStore.setPaymentModalOrder({ type: 'topup' })
}

async function logout() {
    await userStore.logout()
    router.replace({ name: ROUTE_NAMES.MAIN })
}

async function deleteAccount() {
    const isBlocked = await userStore.blockAccount()
    if (!isBlocked) {
        return
    }

    deleteAccountModalOpen.value = false
    router.replace({ name: ROUTE_NAMES.MAIN })
}

function closeNativeAppDebugTool() {
    router.replace({ name: ROUTE_NAMES.PROFILE, query: route.query })
}

onServerPrefetch(async () => {
    await preloadTrendingData()
    if (trendingHasError.value) {
        appStore.setPageStatus(500)
    }
})
</script>
<style module>
.page {
    display: flex;
    flex-direction: column;
    gap: 48px;
    margin-bottom: 48px;
    padding-left: 6px;
    padding-right: 6px;
}

.caption_block {
    padding-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.non_auth_title_page {
    display: flex;
    flex-direction: column;
    align-items: start;
    text-align: start;
    padding-top: 16px;
    gap: 8px;
}

.user_balance {
    flex-grow: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    border: none;
    align-items: center;
    padding-top: 8px;
}

.user_balance__text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    justify-content: center;
}

.user_balance_value {
    line-height: 100%;
}

.page_toggle_override {
    margin-top: 20px;
}

.my_games_grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-flow: dense;
    gap: 8px;
    --cards-gap: 8; /* unitless, consumed by GameCard component */
    position: relative;
}

.logout_button_wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.logout_button_override {
    color: var(--rose-400);
}

.logout_info {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
}

.logout_info_text {
    color: var(--white-60);
    text-align: center;
}

.delete_account_link_override {
    cursor: pointer;
    color: var(--rose-400);
}

.delete_account_link_override:hover {
    filter: brightness(1.2);
}

.buy_button_override {
    width: 100%;
}

.employee_tool_button_override {
    align-self: flex-start;
}

@media (--tablet-plus) {
    .buy_button_override {
        width: auto;
    }

    .logout_button_wrapper {
        flex-direction: row;
        align-items: center;
    }

    .logout_info {
        align-items: flex-start;
        max-width: 310px;
    }

    .logout_info_text {
        text-align: left;
    }

    .caption_block {
        padding-top: 4px;
    }

    .non_auth_title_page {
        padding-top: 4px;
    }

    .my_games_grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (--desktop-small-plus) {
    .user_balance {
        padding-top: 16px;
    }

    .page_toggle_override {
        margin-top: 32px;
    }

    .my_games_grid {
        grid-template-columns: repeat(4, 1fr);
    }

    .page {
        margin-bottom: 80px;
        padding-left: 0;
        padding-right: 6px;
    }
}

.description {
    max-width: 800px;
    color: var(--white-60);
}

.social_logins_override {
    padding-top: 16px;
}

@media (--desktop-medium-plus) {
    .social_logins_override {
        padding-top: 8px;
    }

    .my_games_grid {
        grid-template-columns: repeat(6, 1fr);
    }
}
</style>
